import React, { useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import './PaymentsPlanTab.css';

const STATUS_LABEL = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Payment failed',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  unpaid: 'Unpaid',
};

function formatMoney(cents, currency) {
  if (cents == null) return '—';
  return `${(cents / 100).toFixed(2)} ${(currency || '').toUpperCase()}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

export default function PaymentsPlanTab() {
  const {
    plan, plans, subscription, billingHistory,
    isActive, isPastDue, hasNoSubscription, isLoading,
    startCheckout, cancel,
  } = useSubscription();

  const [railModalOpen, setRailModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [error, setError] = useState(null);

  // Only one plan exists today (see SUPABASE_ADD_SUBSCRIPTIONS_SCHEMA.sql's
  // seed row) — once there's more than one, "Upgrade" needs a plan-picker
  // step before the rail picker below.
  const targetPlan = plans[0];

  const handlePickIntlRail = async () => {
    if (!targetPlan) return;
    setError(null);
    setCheckoutLoading(true);
    try {
      await startCheckout(targetPlan.code);
      // startCheckout redirects the whole page to Stripe on success, so
      // there's normally nothing left to do here.
    } catch (e) {
      setError(e.message || 'Could not start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setError(null);
    setCancelLoading(true);
    try {
      await cancel();
      setCancelModalOpen(false);
    } catch (e) {
      setError(e.message || 'Could not cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="profile-content">
      <div className="profile-header">
        <h1>Payments and plans</h1>
        <p className="profile-header-subtitle">Manage your subscription and billing history.</p>
      </div>

      <div className="profile-main-content">
        <div className="profile-fields-section">
          <div className="settings-section">
            <h2 className="settings-section-title">Current plan</h2>
            <div className="field-content">
              <span className="field-value">
                {isLoading
                  ? 'Loading…'
                  : hasNoSubscription
                    ? 'Free Plan'
                    : plan?.name || 'NOVAS Pro'}
                {!isLoading && !hasNoSubscription && subscription?.status && (
                  <span className={`payments-status-banner ${subscription.status}`}>
                    {STATUS_LABEL[subscription.status] || subscription.status}
                  </span>
                )}
              </span>
              {hasNoSubscription && !isLoading && (
                <button className="upgrade-cta-btn" onClick={() => setRailModalOpen(true)}>Upgrade</button>
              )}
            </div>
            {!hasNoSubscription && subscription?.current_period_end && (
              <p className="field-value" style={{ fontSize: 13, color: '#6c757d' }}>
                {subscription.cancel_at_period_end
                  ? `Access ends on ${formatDate(subscription.current_period_end)}`
                  : `Renews on ${formatDate(subscription.current_period_end)}`}
              </p>
            )}
            {isPastDue && (
              <p className="payments-error">Your last payment failed — please update your payment method to keep access.</p>
            )}
          </div>

          {isActive && !subscription?.cancel_at_period_end && (
            <div className="settings-section">
              <h2 className="settings-section-title">Cancel plan</h2>
              <div className="field-content">
                <span className="field-value">You'll keep access until the end of the current billing period.</span>
                <button className="edit-btn" onClick={() => setCancelModalOpen(true)}>Cancel plan</button>
              </div>
            </div>
          )}

          <div className="settings-section">
            <h2 className="settings-section-title">Billing history</h2>
            {billingHistory.length === 0 ? (
              <div className="field-content">
                <span className="field-value payments-history-empty">No billing history available</span>
              </div>
            ) : (
              <table className="payments-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map((tx) => (
                    <tr key={tx.id}>
                      <td>{formatDate(tx.paid_at || tx.created_at)}</td>
                      <td>{formatMoney(tx.amount_cents, tx.currency)}</td>
                      <td>{tx.status}</td>
                      <td>
                        {tx.invoice_pdf_url && (
                          <a href={tx.invoice_pdf_url} target="_blank" rel="noreferrer">View invoice</a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {error && <p className="payments-error">{error}</p>}
        </div>
      </div>

      {railModalOpen && (
        <div className="payments-rail-overlay" onClick={() => !checkoutLoading && setRailModalOpen(false)}>
          <div className="payments-rail-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Choose how to pay</h3>
            <button
              className="payments-rail-option"
              onClick={handlePickIntlRail}
              disabled={checkoutLoading}
            >
              <span>Pay with international card</span>
              {checkoutLoading && <span className="payments-rail-option-badge">Loading…</span>}
            </button>
            <button className="payments-rail-option" disabled>
              <span>Оплатить российской картой</span>
              <span className="payments-rail-option-badge">Скоро</span>
            </button>
            {error && <p className="payments-error">{error}</p>}
            <button
              className="payments-rail-cancel-link"
              onClick={() => setRailModalOpen(false)}
              disabled={checkoutLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {cancelModalOpen && (
        <div className="payments-rail-overlay" onClick={() => !cancelLoading && setCancelModalOpen(false)}>
          <div className="payments-rail-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel your plan?</h3>
            <p className="field-value">
              You'll keep access until {formatDate(subscription?.current_period_end)}, then your plan won't renew.
            </p>
            {error && <p className="payments-error">{error}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                className="payments-rail-cancel-link"
                onClick={() => setCancelModalOpen(false)}
                disabled={cancelLoading}
                style={{ flex: 1, border: '1px solid #dee2e6', borderRadius: 8 }}
              >
                Keep plan
              </button>
              <button
                className="edit-btn"
                onClick={handleConfirmCancel}
                disabled={cancelLoading}
                style={{ flex: 1 }}
              >
                {cancelLoading ? 'Canceling…' : 'Cancel plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
